import { create } from 'zustand';

import type { Theme } from '@/types';

interface UiState {
    theme: Theme;
}

interface UiActions {
    setTheme: (theme: Theme) => void;
}

type UiStore = UiState & UiActions;

function getInitialTheme(): Theme {
    if (typeof window === 'undefined') {
        return 'dark';
    }

    const storedTheme = localStorage.getItem('theme') as Theme | null;

    if (storedTheme) {
        return storedTheme;
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme): void {
    const rootElement = document.documentElement;
    const resolvedTheme =
        theme === 'system' ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : theme;

    rootElement.classList.remove('light', 'dark');
    rootElement.classList.add(resolvedTheme);
    localStorage.setItem('theme', theme);
}

export const useUiStore = create<UiStore>((set) => {
    const initialTheme = getInitialTheme();

    if (typeof window !== 'undefined') {
        applyTheme(initialTheme);
    }

    return {
        theme: initialTheme,
        setTheme: (theme) => {
            applyTheme(theme);
            set({ theme });
        }
    };
});

export const useTheme = () => useUiStore((state) => state.theme);
export const useSetTheme = () => useUiStore((state) => state.setTheme);
